export const tooltipContent = {
  irr: (
    <span>
      Internal Rate of Return (IRR) is a measure of the rate of return that an
      investment is expected to provide. The IRR is the discount rate that makes the net
      present value (NPV) of all cash flows from a project equal to zero. In other
      words, it is the expected annual rate of return that an investment is expected to
      earn, taking into account the timing and size of the cash flows it generates.
      <br />
      <br />
      Pre-tax IRR assumes that no taxes have been applied to the cash flow model.
      Post-tax IRR assumes taxes have been applied to the cash flow model.
    </span>
  ),
  npv: "Net present value is calculated by discounting all expected future cash flows associated with this project to their present value, and then subtracting the initial investment. If the NPV is positive, the project is considered to be profitable. If the NPV is negative, the project is considered to be unprofitable.",
  co2EmissionsReduction:
    "We calculate the reduction in grid emissions (measured in lbs/kWh) resulting from using a clean energy system to reduce grid-purchased electricity. This calculation is based on the hourly historical energy mix for the region.",
  co2EmissionsReductionPercentage:
    "The estimated CO₂ emissions reduction potential as a percentage of the total annual CO₂ emissions of your viable buildings",
  preTaxIrr: "Incl. SRECs, excl. ITC and depreciation",
  preTaxItcIrr: "Incl. SRECs and ITC, excl. depreciation",
  postTaxItcDeprIrr: "Incl. SRECs, ITC, and depreciation",

  preTaxIrrExpanded: "including SRECs, excluding ITC and depreciation",
  preTaxItcIrrExpanded: "including SRECs and ITC, excluding depreciation",
  postTaxItcDeprIrrExpanded: "including SRECs, ITC, and depreciation",

  preTaxIrrLabel: "Pre-tax IRR excl. ITC",
  preTaxItcIrrLabel: "Pre-tax IRR incl. ITC",
  postTaxItcDeprIrrLabel: "Post-tax IRR incl. ITC",

  preTaxIrrLabelExpanded: "Pre-tax IRR excluding ITC",
  preTaxItcIrrLabelExpanded: "Pre-tax IRR including ITC",
  postTaxItcDeprIrrLabelExpanded: "Post-tax IRR including ITC",

  investorViableProperties:
    "Number of active properties with available offers or marked for cash purchase",
  offtakerViableProperties: "Number of active properties with available offers",

  // Financing Comparison
  upfrontCostBeforeIncentives:
    "The total initial investment required, including equipment and installation costs, before applying any incentives or rebates. For PPAs, this value is $0 since financiers invest the upfront capital.",
  upfrontCostAfterIncentives:
    "The net initial investment after deducting available incentives and rebates, showing the true initial cost of the project. For PPAs, this value is $0 since financiers invest the upfront capital.",
  netCashFlowYearOne: (
    <>
      <p>
        PPAs &mdash; The overall financial impact in the first year of the PPA,
        including energy savings, site lease revenue, and any ongoing costs or fees
        associated with the agreement.
      </p>
      <br />
      <p>
        Cash purchase &mdash; The projected post-tax financial gain or loss in the first
        year of the system&rsquo;s lifetime, net of install cost and including
        depreciation and incentives.
      </p>
    </>
  ),
  netCashFlowLifetime: (
    <>
      <p>
        PPAs &mdash; The overall financial impact over the lifetime of the PPA,
        including energy savings, site lease revenue, and any ongoing costs or fees
        associated with the agreement.
      </p>
      <br />
      <p>
        Cash purchase &mdash; The total projected post-tax financial gain or loss over
        the system&rsquo;s lifetime, net of install cost and including depreciation and
        incentives.
      </p>
    </>
  ),
  paybackPeriod: (
    <p>
      The average time it takes for energy savings, site lease revenue, and incentives
      to cover the initial project costs, reflecting the investment&rsquo;s break-even
      point. For PPAs, there is no initial investment to recoup.
    </p>
  ),
  buildingOwnerNpv: (
    <>
      <p>
        PPA &mdash; The present value of net savings over the contract term, accounting
        for energy cost trends and discount rates. Reflects the long-term financial
        benefit of the PPA.
      </p>
      <br />
      <p>
        Cash purchase &mdash; The net present value of all future, post-tax cash flows
        over the system&rsquo;s lifetime, net of install cost and including depreciation
        and incentives.
      </p>
    </>
  ),
  relativeRisk: (
    <>
      <p>
        PPA &mdash; Building owners lock in electricity prices at a fixed rate, hedging
        against unpredictable utility rate hikes. Financing, construction, and operation
        risks are managed by the financier.
      </p>
      <br />
      <p>
        Cash purchase &mdash; Building owners take on project execution risk for
        financing, constructing, and operating the project.
      </p>
    </>
  ),

  // Data collection
  buildingStatus: (
    <p>
      <strong>Active:</strong> Under evaluation for solar and storage
      <br />
      <strong>On hold:</strong> Evaluation is currently paused
      <br />
      <strong>Archived:</strong> Deemed unsuitable for solar for now
    </p>
  ),
  projectStatus: (
    <p>
      For projects being evaluated for PPAs, project status is automatically updated by
      Lumen and can be one of:
      <br />
      <strong>Estimated offer:</strong> Property evaluation reflects automated estimates
      <br />
      <strong>Verified offer:</strong> Property evaluation reflects user-provided
      property data, actual energy data and Helioscope designs
      <br />
      <strong>Final offer: </strong> Property evaluation has been further refined based
      on building owner feedback
      <br />
      <strong>Term sheet:</strong> Property is at the stage to review a term sheet
      <br />
      <strong>PPA contract:</strong> PPA signatory is reviewing the PPA contract
      <br />
      <strong>No offer available: </strong> Property has been deemed unsuitable for a
      PPA
    </p>
  ),
  buildingType: (
    <p>
      Building type is an input to estimate electricity consumption. There are 11
      possible building types: Office, Multi-Family, Flex, Hospitality, Industrial,
      Land, Retail, Shopping Center, Health Care, Specialty, as well as Sports &
      Entertainment.
      <br />
      <br />
      We try to pull a default value from an industry leading commercial & industrial
      building dataset, but please confirm.
    </p>
  ),
  buildingSubtype: (
    <p>
      Building subtype is an input to estimate electricity consumption.
      <br />
      <br />
      We try to pull a default value from an industry leading commercial & industrial
      building dataset, but please confirm.
    </p>
  ),
  salePlans: (
    <p>
      Let us know if there are plans to sell this property within the next 5 years. Most
      customers deprioritize buildings to be sold in the next 5 years.
      <br />
      <br />
      Options: Yes, No, Unsure.
    </p>
  ),
  saleDate: (
    <p>
      Please share the expected year of property sale. Leave blank if there are no plans
      to sell.
      <br />
      <br />
      We likely won`t recommend PPAs on properties being sold in the next 5 years.
    </p>
  ),
  solarLocations: (
    <p>
      Please select all locations where Lumen may consider placing solar panels. By
      default, we assume the rooftop is available.
      <br />
      <br />
      Options: Building roof, Parking garage roof, Outdoor parking, Other.
    </p>
  ),
  solarLocationsOther: (
    <p>Let us know where else we may consider placing solar panels.</p>
  ),
  mortgageLender: (
    <p>
      Some mortgage lenders are more comfortable with clean energy installations than
      others. We encourage you to check that your lender would sign an SNDA for a solar
      asset at this property. Let us know who yours is, and we can check our database in
      the meantime.
    </p>
  ),
  numStories: (
    <p>
      Number of stories is an input to estimate installation costs, as taller buildings
      typically require more complex construction equipment and skilled labor.
      <br />
      <br />
      We try to pull a default value from an industry leading commercial & industrial
      building dataset, but please confirm.
    </p>
  ),
  yearBuilt: (
    <p>
      Year built is an input to estimate electricity consumption and can serve as a
      proxy for roof age.
      <br />
      <br />
      We try to pull a default value from an industry leading commercial & industrial
      building dataset, but please confirm.
    </p>
  ),
  buildingFootprintSqft: (
    <p>
      Building square footage is an input to estimate the building&apos;s electricity
      consumption.
      <br />
      <br />
      We try to pull a default value from an industry leading commercial & industrial
      building dataset, but please confirm.
    </p>
  ),
  qualificationForm: (
    <p>
      Answer required questions directly via a form, that you can also share with your
      colleagues.
    </p>
  ),
  loadKwh: (
    <p>
      Total annual electricity usage, summed across all meters.
      <br />
      <br />
      All values have been adjusted to the current year, so they may not precisely match
      submitted bills.
    </p>
  ),
  loadUsd: <p>Total pre-solar annual electricity cost, summed across all meters.</p>,
  loadUsdPerKwh: (
    <p>
      Total pre-solar annual electricity cost divided by usage, computed across all
      meters. High $/kWh may indicate an opportunity.
      <br />
      <br />
      Electricity cost varies:
      <br />
      • $/kWh is generally higher when consumption is low due to volumetric discounts;
      <br />• Charges may vary according to time of use (ToU) or load shape (demand);
      <br />• Residential costs are typically higher than commercial costs due to
      utility rate design practices.
    </p>
  ),
  loadOrigin: (
    <p>
      Precision of the annual electricity consumption number.
      <br />
      <br />
      We initially use a proprietary algorithm to estimate the property’s electricity
      costs. We can refine this estimate based on annual or monthly numbers you provide,
      or replace it with intervals recorded by an electricity meter.
    </p>
  ),
  utilityCagr: (
    <p>
      The compound annual growth rate (CAGR) of the cost of electricity sold by utility,
      blended across all sectors.
      <br />
      <br />
      We derive this value from data reported to the U.S. Energy Information
      Administration. It is not available for certain utilities.
    </p>
  ),
  metering: (
    <p>
      Let us know how this property is metered for electricity.
      <br />
      <br />
      Options:
      <br />
      <strong>Master metered:</strong> entire property is metered via one master meter
      that the building owner pays
      <br />
      <strong>Sub metered:</strong> tenants metered individually by the building owner
      and pay the building owner for their electricity
      <br />
      <strong>Utility (direct) metered:</strong> tenants have individual electric meters
      and pay directly to utility company
      <br />
      <strong>Not sure</strong>
    </p>
  ),
  roofCondition: (
    <p>
      Let us know the condition of the roof. If the roof is in poor condition, we may
      recommend doing a roof upgrade alongside the solar installation.
    </p>
  ),
  roofReplacedYear: (
    <p>
      Let us know when the roof was last replaced. Leave blank if unsure or never
      replaced. If the roof is in poor condition, we may recommend doing a roof upgrade
      alongside the solar installation.
    </p>
  ),
  roofSqFeet: (
    <p>
      Let us know the square footage available for solar panels. This value determines
      the maximum system size that is possible to be installed.
      <br />
      <br />
      By default, we assume 70% of the roof is available to install solar, but some
      roofs are more or less crowded.
    </p>
  ),
  ppaOfftaker: (
    <p>
      For non-multifamily properties, let us know if the offtaker of Power Purchase
      Agreement will be the building owner, tenants, or unknown. Leave blank if not
      considering a PPA.
      <br />
      <br />
      By default, we don`t make an assumption as to who the offtaker will be.
    </p>
  ),
  ppaOfftakerName: (
    <p>
      Let us know the name(s) of the entity that would sign a Power Purchase Agreement
      for this property. Leave blank if not considering a PPA.
    </p>
  ),
  occupancyType: (
    <p>
      Let us know who the occupants are in the property.
      <br />
      <br />
      Options:
      <br />
      <strong>Solely owner occupied</strong>
      <br />
      <strong>Single third party tenant</strong>
      <br />
      <strong>Multiple third party tenants</strong>
      <br />
      <strong>Unsure</strong>
    </p>
  ),
  leaseTerm: <p>Let us know the remaining term of the shortest current lease.</p>,
  leaseType: (
    <p>
      For non-multifamily properties, let us know the lease type for the tenants at this
      property. Leave blank if you don&apos;t have tenants.
      <br />
      <br />
      Options:
      <br />
      <strong>Gross lease</strong>
      <br />
      <strong>Net lease</strong>
      <br />
      <strong>Double net lease (NN)</strong>
      <br />
      <strong>Triple net lease (NNN)</strong>
    </p>
  ),
  tenantInterest: (
    <p>
      Let us know if your tenants are interested in a PPA. We use this information to
      help prioritize properties and engage tenants in the data collection process.
      Leave blank if you don&apos;t have tenants or aren&apos;t considering a PPA.
      <br />
      <br />
      Options:
      <br />
      Yes
      <br />
      No
      <br />
      Unsure
    </p>
  ),
}
